/**
 * Horizontal Carousel
 * ----------------------------------------------------------------------
 * Baseline (no JS): plain overflow-x:auto scrolling — always reachable.
 *
 * Enhanced (this file): position is driven entirely by `transform`
 * inside a requestAnimationFrame loop, with ONE velocity/friction system
 * feeding drag release, mouse-wheel/trackpad input, and idle auto-scroll.
 * That's what makes it feel consistent and smooth across input types —
 * native `scrollLeft` + CSS scroll-snap fighting each other is what
 * caused the old "works sometimes" behaviour.
 *
 * - Drag (mouse or touch, via Pointer Events) moves 1:1 with the pointer.
 * - Wheel/trackpad input adds an impulse to velocity; friction smooths it.
 * - Release carries the drag's trailing velocity into the same friction
 *   system (momentum).
 * - When idle for a bit, a constant auto-scroll velocity nudges the
 *   track forward and simply stops once it reaches the last slide — it
 *   does not loop back to the start.
 * - Position is hard-clamped between the first and last slide at all
 *   times, so it always "stops after the last slide."
 * ----------------------------------------------------------------------
 */
(function () {
  'use strict';

  var POINTER_FINE = '(pointer: fine)';
  var REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

  var FRICTION = 0.94;
  var WHEEL_SCALE = 0.6;
  var MAX_VELOCITY = 60;
  var CLICK_THRESHOLD = 6; // px of movement before a drag suppresses the link click
  var AUTO_RESUME_DELAY = 2500; // ms of inactivity before auto-scroll resumes
  var STOP_VELOCITY = 0.03;

  var viewports = document.querySelectorAll('[data-carousel-viewport]');
  if (!viewports.length) return;

  viewports.forEach(initCarousel);

  function initCarousel(viewport) {
    var root = viewport.closest('.horizontal-carousel');
    var track = viewport.querySelector('[data-carousel-track]');
    var mediaEls = viewport.querySelectorAll('[data-carousel-media]');
    if (!track || !root) return;

    var sensitivity = parseFloat(viewport.dataset.wheelSensitivity || '1') || 1;
    var autoScrollEnabled = viewport.dataset.autoscroll === 'true';
    var autoScrollSpeed = (parseFloat(viewport.dataset.autoscrollSpeed || '30') || 30) / 60; // px/frame

    var pointerFine = window.matchMedia(POINTER_FINE);
    var reducedMotion = window.matchMedia(REDUCED_MOTION);

    var x = 0;
    var velocity = 0;
    var minX = 0;
    var dragging = false;
    var startClientX = 0;
    var startX = 0;
    var lastClientX = 0;
    var lastMoveTime = 0;
    var maxMove = 0;
    var wasDragged = false;
    var dragCandidate = false;
    var activePointerId = null;
    var isHovering = false;
    var lastInteraction = 0;
    var inView = false;
    var rafId = null;

    function clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    function measure() {
      var overflow = track.scrollWidth - viewport.clientWidth;
      minX = overflow > 0 ? -overflow : 0;
      x = clamp(x, minX, 0);
    }

    function enhance() {
      root.classList.add('is-enhanced');
      viewport.scrollLeft = 0;
      measure();
      startLoop();
    }

    // ---- Pointer drag (mouse + touch, unified) ----
    // A pointerdown only opens a "candidate" — it does NOT start dragging or
    // capture the pointer yet. That's deliberate: capturing immediately on
    // every pointerdown (including a plain tap on a slide's link) is what
    // broke link clicks. We only commit to an actual drag, and only then
    // capture the pointer, once movement crosses CLICK_THRESHOLD. A normal
    // tap/click never reaches that branch, so it's never touched.

    viewport.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      dragCandidate = true;
      activePointerId = event.pointerId;
      maxMove = 0;
      startClientX = event.clientX;
      startX = x;
      lastClientX = event.clientX;
      lastMoveTime = performance.now();
      velocity = 0;
    });

    viewport.addEventListener('pointermove', function (event) {
      if (!dragCandidate || event.pointerId !== activePointerId) return;
      var now = performance.now();
      var dx = event.clientX - startClientX;
      maxMove = Math.max(maxMove, Math.abs(dx));

      if (!dragging) {
        if (maxMove <= CLICK_THRESHOLD) return; // still just a tap/jitter, not a drag
        dragging = true;
        viewport.classList.add('is-dragging');
        try {
          viewport.setPointerCapture(event.pointerId);
        } catch (err) {
          /* no-op: pointer capture isn't critical */
        }
      }

      x = clamp(startX + dx, minX, 0);
      var dt = now - lastMoveTime;
      if (dt > 0) {
        velocity = ((event.clientX - lastClientX) / dt) * 16.67;
      }
      lastClientX = event.clientX;
      lastMoveTime = now;
      lastInteraction = now;
    });

    function endDrag() {
      dragCandidate = false;
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');
      wasDragged = true; // reaching here means the threshold was crossed
      velocity = clamp(velocity, -MAX_VELOCITY, MAX_VELOCITY);
      lastInteraction = performance.now();
    }

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);

    // Suppress a slide's link navigation only if that pointer sequence was
    // an actual drag (never true for a plain tap/click).
    viewport.addEventListener(
      'click',
      function (event) {
        if (wasDragged) {
          event.preventDefault();
          event.stopPropagation();
          wasDragged = false;
        }
      },
      true
    );

    // ---- Wheel/trackpad: adds an impulse; friction does the smoothing ----

    viewport.addEventListener(
      'wheel',
      function (event) {
        if (!pointerFine.matches) return;
        var delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
        if (!delta) return;
        event.preventDefault();
        velocity = clamp(velocity - delta * sensitivity * WHEEL_SCALE, -MAX_VELOCITY, MAX_VELOCITY);
        lastInteraction = performance.now();
      },
      { passive: false }
    );

    // ---- Keyboard ----

    viewport.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      var step = viewport.clientWidth * 0.6;
      var impulse = step * 0.15 * (event.key === 'ArrowRight' ? -1 : 1);
      velocity = clamp(velocity + impulse, -MAX_VELOCITY, MAX_VELOCITY);
      lastInteraction = performance.now();
    });

    // ---- Hover pauses auto-scroll immediately ----

    viewport.addEventListener('pointerenter', function () {
      isHovering = true;
      lastInteraction = performance.now();
    });
    viewport.addEventListener('pointerleave', function () {
      isHovering = false;
      lastInteraction = performance.now();
    });

    // ---- Main loop ----

    function frame() {
      var now = performance.now();

      if (!dragging) {
        if (Math.abs(velocity) > STOP_VELOCITY) {
          x = clamp(x + velocity, minX, 0);
          velocity *= reducedMotion.matches ? 0 : FRICTION;
          if (Math.abs(velocity) <= STOP_VELOCITY) velocity = 0;
        } else if (
          autoScrollEnabled &&
          !reducedMotion.matches &&
          !isHovering &&
          x > minX &&
          now - lastInteraction > AUTO_RESUME_DELAY
        ) {
          x = clamp(x - autoScrollSpeed, minX, 0);
        }
      }

      track.style.transform = 'translate3d(' + x.toFixed(2) + 'px,0,0)';
      rafId = requestAnimationFrame(frame);
    }

    function startLoop() {
      if (rafId) return;
      rafId = requestAnimationFrame(frame);
    }

    function stopLoop() {
      if (!rafId) return;
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    // Only animate while the carousel is actually visible.
    var inViewObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          inView = entry.isIntersecting;
          track.classList.toggle('will-animate', inView);
          if (inView) startLoop();
          else stopLoop();
        });
      },
      { rootMargin: '20% 0px 20% 0px' }
    );
    inViewObserver.observe(root);

    function refresh() {
      measure();
    }
    window.addEventListener('load', refresh);
    window.addEventListener('resize', debounce(refresh, 150));
    if ('ResizeObserver' in window) {
      new ResizeObserver(debounce(refresh, 150)).observe(track);
    }

    enhance();

    // ---- Lazy media: hydrate + play/pause based on visibility ----
    if (!mediaEls.length) return;

    function hydrateMedia(el) {
      if (el.dataset.hydrated) return;
      el.dataset.hydrated = 'true';
      if (el.tagName === 'VIDEO') {
        el.querySelectorAll('source[data-src]').forEach(function (source) {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        // Belt-and-suspenders on top of the `loop` attribute: some browsers
        // can end up pausing a video right as it reaches the end (e.g. an
        // intersection check landing at that exact moment), which can leave
        // it sitting on the last frame instead of looping. This guarantees
        // it always restarts from the top.
        el.addEventListener('ended', function () {
          el.currentTime = 0;
          playVideo(el);
        });
        el.load();
      } else if (el.tagName === 'IMG' && el.dataset.src) {
        el.src = el.dataset.src;
        el.removeAttribute('data-src');
      }
    }

    function playVideo(el) {
      if (!el.paused) return;
      var playPromise = el.play();
      if (playPromise && playPromise.catch) playPromise.catch(function () {});
    }

    function syncMedia() {
      mediaEls.forEach(function (el) {
        var visible = el.offsetParent !== null;
        if (visible) hydrateMedia(el);
        else if (el.tagName === 'VIDEO') el.pause();
      });
    }

    var mediaObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var el = entry.target;
          var visible = el.offsetParent !== null;
          if (entry.isIntersecting && visible) {
            hydrateMedia(el);
            if (el.tagName === 'VIDEO') playVideo(el);
          } else if (el.tagName === 'VIDEO') {
            el.pause();
          }
        });
      },
      { root: viewport, rootMargin: '200px', threshold: 0.01 }
    );
    mediaEls.forEach(function (el) {
      mediaObserver.observe(el);
    });

    window.addEventListener('resize', debounce(syncMedia, 150));
    window.addEventListener('load', syncMedia);
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }
})();